import { useState } from 'react'
import { router } from '@inertiajs/react'
import {
  CalendarIcon,
  ClockIcon,
  UserIcon,
  MapPinIcon,
  FileTextIcon,
  Trash2Icon,
  PencilLineIcon,
  MailIcon,
  FileIcon,
} from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { DeleteConfirmationDialog } from '@/components/delete-confirmation-dialog'

interface InternalAppointmentDetailDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  appointment: {
    id: number | string
    lead_id?: number
    lead_name?: string
    date: string
    time: string
    duration: number
    title?: string
    appointment_type: string
    technician?: string
    organizer?: string
    participants?: string[]
    status: string
    notes?: string
    location?: string
    synced_to_google: boolean
    created_at: string
  } | null
}

export function InternalAppointmentDetailDialog({
  open,
  onOpenChange,
  appointment,
}: InternalAppointmentDetailDialogProps) {
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)

  if (!appointment) return null

  const handleDelete = () => {
    router.delete(`/appointments/${appointment.id}`, {
      onSuccess: () => {
        onOpenChange(false)
        // Refresh the calendar view
        router.reload()
      },
    })
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString + 'T00:00:00')
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    })
  }

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between pr-8">
              <div>
                <div className="text-xl font-bold">
                  {appointment.title || appointment.appointment_type}
                </div>
                <p className="text-sm text-muted-foreground font-normal mt-1">
                  From LeadFlow CRM
                </p>
              </div>
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-6">
            {/* Appointment Information */}
            <div className="space-y-4">

              {/* Date */}
              <div className="flex items-start gap-3">
                <CalendarIcon className="size-5 text-muted-foreground mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Date</p>
                  <p className="mt-1">{formatDate(appointment.date)}</p>
                </div>
              </div>

              {/* Time */}
              <div className="flex items-start gap-3">
                <ClockIcon className="size-5 text-muted-foreground mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Time</p>
                  <p className="mt-1">
                    {appointment.time} ({appointment.duration} minutes)
                  </p>
                </div>
              </div>

              {/* Location - Always show */}
              <div className="flex items-start gap-3">
                <MapPinIcon className="size-5 text-muted-foreground mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Location</p>
                  <p className="mt-1">{appointment.location || 'Not specified'}</p>
                </div>
              </div>

              {/* Organizer - Always show, include lead name if present */}
              <div className="flex items-start gap-3">
                <UserIcon className="size-5 text-muted-foreground mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Organizer</p>
                  <p className="mt-1">{appointment.organizer || 'Not specified'}</p>
                  {appointment.lead_name && (
                    <p className="mt-1 text-sm text-muted-foreground">
                      Lead: {appointment.lead_name}
                    </p>
                  )}
                </div>
              </div>

              {/* Technician - Always show */}
              <div className="flex items-start gap-3">
                <UserIcon className="size-5 text-muted-foreground mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Technician</p>
                  <p className="mt-1">{appointment.technician || 'Not assigned'}</p>
                </div>
              </div>

              {/* Notes/Description */}
              {appointment.notes && (
                <div className="flex items-start gap-3">
                  <FileTextIcon className="size-5 text-muted-foreground mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-muted-foreground mb-2">Notes</p>
                    <p className="whitespace-pre-wrap rounded-md bg-muted p-3 text-sm">
                      {appointment.notes}
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Participants */}
            {appointment.participants && appointment.participants.length > 0 && (
              <div>
                <p className="text-sm font-medium mb-3">Participants ({appointment.participants.length})</p>
                <div className="space-y-2">
                  {appointment.participants.map((email, index) => (
                    <div key={index} className="flex items-center gap-2 py-2 border-b last:border-b-0">
                      <MailIcon className="size-4 text-muted-foreground" />
                      <span className="text-sm">{email}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="flex justify-end gap-2 pt-4 border-t">
              <Button
                variant="outline"
                onClick={() => {
                  // TODO: Add edit functionality
                }}
                data-testid="edit-appointment-button"
              >
                <PencilLineIcon className="mr-2 size-4" />
                Edit
              </Button>
              <Button
                variant="destructive"
                onClick={() => setDeleteDialogOpen(true)}
                data-testid="delete-appointment-button"
              >
                <Trash2Icon className="mr-2 size-4" />
                Delete
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <DeleteConfirmationDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        onConfirm={handleDelete}
        title="Delete Appointment"
        description="Are you sure you want to delete this appointment? This action cannot be undone."
      />
    </>
  )
}
